import { UserCommunication } from '@this+that/models';
import { StoreApi } from 'zustand';

import { InstagramCommunicationsState, InstagramConversation } from './types';
import { InstagramConversation as InstagramConversationView } from '../../../services/meta.service';
import { sortByLastMessage } from '../utils';

type ZustandSet = StoreApi<InstagramCommunicationsState>['setState'];
type ZustandGet = StoreApi<InstagramCommunicationsState>['getState'];

const updateConversation = (
  conversations: InstagramConversation[],
  conversationId: string,
  updater: (communications: UserCommunication[]) => UserCommunication[],
): InstagramConversation[] =>
  conversations.map((conversation) => {
    if (conversation.conversationId !== conversationId) {
      return conversation;
    }

    const communications = updater(conversation.communications);

    return { ...conversation, communications, lastMessage: communications[0] };
  });

export const handleSetLoading = (set: ZustandSet, isLoading: boolean) => {
  set({ isLoading });
};

export const handleSaveInstagramConversations = (
  set: ZustandSet,
  incoming: InstagramConversationView[],
) => {
  set((state) => {
    const conversations = incoming.map((item) => {
      const previous = state.conversations.find(
        (conversation) => conversation.conversationId === item.conversationId,
      );

      return {
        ...previous,
        ...item,
        communications: previous?.communications ?? [],
        lastMessage: item.lastCommunication ?? previous?.lastMessage,
      } as InstagramConversation;
    });

    return { conversations: conversations.sort(sortByLastMessage) };
  });
};

export const handleSetConversationCommunications = (
  set: ZustandSet,
  get: ZustandGet,
  conversationId: string,
  communications: UserCommunication[],
) => {
  set({
    conversations: updateConversation(
      get().conversations,
      conversationId,
      () => communications,
    ).sort(sortByLastMessage),
  });
};

export const handleAddInstagramCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  conversationId: string,
  communication: UserCommunication,
) => {
  const { conversations } = get();

  const isDuplicate = conversations.some(
    (conversation) =>
      conversation.conversationId === conversationId &&
      conversation.communications.some(
        (existing) => existing.messageId === communication.messageId,
      ),
  );

  if (isDuplicate) {
    return;
  }

  set({
    conversations: updateConversation(
      conversations,
      conversationId,
      (communications) => [communication, ...communications],
    ).sort(sortByLastMessage),
  });
};

export const handleUpdateInstagramCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  conversationId: string,
  messageId: string,
  updatedFields: Partial<UserCommunication>,
) => {
  set({
    conversations: updateConversation(
      get().conversations,
      conversationId,
      (communications) =>
        communications.map((communication) =>
          communication.messageId === messageId
            ? { ...communication, ...updatedFields }
            : communication,
        ),
    ).sort(sortByLastMessage),
  });
};

export const handleDeleteInstagramCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  conversationId: string,
  messageId: string,
) => {
  set({
    conversations: updateConversation(
      get().conversations,
      conversationId,
      (communications) =>
        communications.filter(
          (communication) => communication.messageId !== messageId,
        ),
    ).sort(sortByLastMessage),
  });
};
